/**
 * FileLinks — the files a record holds (a `file` fact or column, a timeline event's file, a dry run's file), each its
 * name as a button, comma separated, with its size where known. A click fetches the file with the person's sign-in
 * (`ObjectUiAdapter.download`), never by pointing the browser at the API path, then opens a PDF or an image in a new
 * tab and saves anything else, through the capability ports. A file that cannot be fetched says why under it. On a
 * list the row around it also opens its own record, and must not (stopPropagation).
 */
import { useState } from "react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type { FileValue } from "@shared-core/domain/object-ui/contract";
import {
  fileOpenFailure,
  opensInNewTab,
} from "@shared-core/domain/object-ui/files";
import { formatBytes } from "@shared-core/domain/formatters";

export function FileLinks({ files }: { files: readonly FileValue[] }) {
  const adapter = useObjectUiAdapter();
  const capabilities = usePlatformCapabilities();
  const [openingHref, setOpeningHref] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const open = async (file: FileValue) => {
    setFailure(null);
    setOpeningHref(file.href);
    try {
      const { blob } = await adapter.download(file.href);
      if (opensInNewTab(blob.type, file.name))
        capabilities.openBlobInNewTab(blob);
      else capabilities.downloadBlob(file.name, blob);
    } catch (error) {
      console.warn("object_ui.file.open_failed", {
        status: error instanceof ApiError ? error.status : null,
      });
      setFailure(fileOpenFailure(error, file.name));
    } finally {
      setOpeningHref(null);
    }
  };

  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span>
        {files.map((file, i) => (
          <span key={`${file.href}-${i}`}>
            {i > 0 && ", "}
            <button
              type="button"
              // [font:inherit]: theme.css gives every button text-base and medium weight; a file link reads like the
              // links around it.
              className="text-left [font:inherit] text-primary underline-offset-2 hover:underline disabled:cursor-wait disabled:opacity-60"
              disabled={openingHref === file.href}
              onClick={(e) => {
                e.stopPropagation();
                void open(file);
              }}
            >
              {file.name}
            </button>
            {file.size_bytes != null && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {formatBytes(file.size_bytes)}
              </span>
            )}
          </span>
        ))}
      </span>
      {failure && (
        <span role="alert" className="text-sm text-red-600">
          {failure}
        </span>
      )}
    </span>
  );
}
